import {useMemo, useState} from 'react'

import invariant from 'invariant'
import keyBy from 'lodash/keyBy'
import pick from 'lodash/pick'

import {formatDeliveryTime} from '../libs/formatter'
import {getProductionTime} from '../libs/order'

import {Provider} from '../types'

import topics from '../assets/json/message-templates/level1.json'
import topicTemplates from '../assets/json/message-templates/level2.json'
import actionTemplates from '../assets/json/message-templates/level3.json'

import {Order} from '../order-types'

type TopicKey = string
type TopicTemplateKey = string
type TopicActionSuggestionKey = string

export type Topic = {
  key: TopicKey
  label: string
  templates: TopicTemplateKey[]
}

export type TopicTemplate = {
  key: TopicTemplateKey
  label: string
  text: string
  actionSuggestions?: TopicActionSuggestionKey[]
}

export type TopicActionSuggestion = {
  key: TopicActionSuggestionKey
  label: string
  text: string
}

export type TopicTemplateOption = {
  value: string
  label: string
  template: TopicTemplate
  hasActionSuggestions: boolean
  needsFileNameInput: boolean
  uneditable?: boolean
}

export type TopicOption = {
  value: string
  label: string
  topic: Topic
}

export type ActionSuggestionOption = {
  value: string
  label: string
  suggestion: TopicActionSuggestion
  needsFileNameInput: boolean
}

export type MentionedFileNameOption = {
  value: string
  label: string
}

enum Placeholder {
  'CUSTOMER_NAME' = '[autofill: customer name]',
  'PARTNER_NAME' = '[autofill: manufacturer name]',
  'CURRENCY' = '[autofill: currency]',
  'TRACKING_NUMBER' = '[autofill: tracking number]',
  'PRODUCTION_LEADTIME' = '[autofill: production leadtime]',
  'FILE_NAME' = '[choose: fileName]',
  'CHOOSE_ACTION' = '[choose: a template from our "Suggested action" drop-down menu above]',
}

const actionSuggestionOptions: ActionSuggestionOption[] = actionTemplates.map(suggestion => ({
  value: suggestion.key,
  label: suggestion.label,
  suggestion,
  needsFileNameInput: suggestion.text.includes(Placeholder.FILE_NAME),
}))
const actionTemplatesByKey = keyBy(actionSuggestionOptions, 'value')

const topicTemplateOptions: TopicTemplateOption[] = topicTemplates.map(template => ({
  value: template.key,
  label: template.label,
  template,
  hasActionSuggestions: !!template.actionSuggestions && template.actionSuggestions.length > 0,
  needsFileNameInput: template.text.includes(Placeholder.FILE_NAME),
  uneditable: !!template.uneditable,
}))
const topicTemplatesByKey = keyBy(topicTemplateOptions, 'value')

const topicOptions: TopicOption[] = topics.map(topic => ({
  value: topic.key,
  label: topic.label,
  topic,
}))

const useMessageTemplate = (order: Order, provider: Provider | null) => {
  const [topic, setTopic] = useState<TopicOption>()
  const [topicTemplate, setTopicTemplate] = useState<TopicTemplateOption>()
  const [currentTopicTemplateOptions, setCurrentTopicTemplateOptions] = useState<
    TopicTemplateOption[]
  >([])
  const [actionSuggestion, setActionSuggestion] = useState<ActionSuggestionOption>()
  const [currentActionOptions, setCurrentActionOptions] = useState<ActionSuggestionOption[]>([])
  const [mentionedFileNames, setMentionedFileNames] = useState<MentionedFileNameOption[]>([])

  invariant(provider, 'No provider found for message templates')

  const buildMessage = (templateOption: TopicTemplateOption) => {
    const message = templateOption.template.text

    // Add optional action suggestion
    const messageWithActionSuggestion = message.replace(
      Placeholder.CHOOSE_ACTION,
      actionSuggestion ? actionSuggestion.suggestion.text : Placeholder.CHOOSE_ACTION
    )

    // Replace placeholders
    return messageWithActionSuggestion
      .replace(Placeholder.CUSTOMER_NAME, `${order.customer.firstName} ${order.customer.lastName}`)
      .replace(Placeholder.PARTNER_NAME, provider.name)
      .replace(Placeholder.CURRENCY, order.customer.currency)
      .replace(
        Placeholder.TRACKING_NUMBER,
        order.printingService.trackingNumber || '[insert: tracking number]'
      )
      .replace(Placeholder.PRODUCTION_LEADTIME, formatDeliveryTime(getProductionTime(order.quotes)))
      .replace(
        Placeholder.FILE_NAME,
        mentionedFileNames.length > 0
          ? mentionedFileNames.map(mentionedFile => `"${mentionedFile.label}"`).join(', ')
          : Placeholder.FILE_NAME
      )
  }

  const resetTemplates = () => {
    setTopic(undefined)
    setTopicTemplate(undefined)
    setActionSuggestion(undefined)
    setMentionedFileNames([])
  }

  const onTopicChange = (option: TopicOption) => {
    setTopic(option)
    const newTopicTemplateOptions = Object.values(pick(topicTemplatesByKey, option.topic.templates))
    setCurrentTopicTemplateOptions(newTopicTemplateOptions)
    // reset the rest
    setTopicTemplate(undefined)
    setActionSuggestion(undefined)
    setMentionedFileNames([])
  }

  const onTopicTemplateChange = (option: TopicTemplateOption) => {
    setTopicTemplate(option)
    if (option.hasActionSuggestions) {
      const newActionSuggestionsTemplates = Object.values(
        pick(actionTemplatesByKey, option.template.actionSuggestions as TopicActionSuggestionKey[])
      )
      setCurrentActionOptions(newActionSuggestionsTemplates)
    }
    // reset the rest
    setActionSuggestion(undefined)
    setMentionedFileNames([])
  }

  const onActionSuggestionChange = (option: ActionSuggestionOption) => {
    setActionSuggestion(option)
    setMentionedFileNames([])
  }

  const onMentionedFileNamesChange = (option: MentionedFileNameOption[]) => {
    setMentionedFileNames(option)
  }

  const isCustomMessage = (message: string) =>
    message !== '' && !!topicTemplate && message !== buildMessage(topicTemplate)

  const hasRemainingPlaceholders = (message: string) => {
    const regex = /\[.*?\]/g // find everything between two brackets ("[" & "]")
    const remainingPlaceholders = message.match(regex)
    return !!remainingPlaceholders
  }

  const mentionsAttachment = (message: string) => message.includes('picture attached')

  const showTopicTemplateInput = useMemo(() => !!topic, [topic])

  const showActionSuggestionInput = useMemo(
    () => !!topicTemplate && topicTemplate.hasActionSuggestions,
    [topicTemplate]
  )

  const showAffectedFilesInput = useMemo(
    () =>
      !!topicTemplate &&
      (topicTemplate.needsFileNameInput ||
        (!!actionSuggestion && actionSuggestion.needsFileNameInput)),
    [topicTemplate, actionSuggestion]
  )

  return {
    topic,
    onTopicChange,
    topicTemplate,
    onTopicTemplateChange,
    actionSuggestion,
    onActionSuggestionChange,
    mentionedFileNames,
    onMentionedFileNamesChange,
    currentTopicOptions: topicOptions,
    currentTopicTemplateOptions,
    currentActionOptions,
    templateHelper: {
      buildMessage,
      resetTemplates,
      isCustomMessage,
      hasRemainingPlaceholders,
      mentionsAttachment,
    },
    showTopicTemplateInput,
    showActionSuggestionInput,
    showAffectedFilesInput,
  }
}

export default useMessageTemplate
